/* eslint react-hooks/exhaustive-deps: "warn" */

import {
  CSSProperties,
  PointerEvent as ReactPointerEvent,
  MouseEvent as ReactMouseEvent,
  useEffect,
  useRef,
  useState,
} from 'react';

import styles from './MetadataWidget.module.scss';

import type { MetadataNodeProps, MetadataWidgetProps } from './MetadataWidget.types';

const STORAGE_KEY = 'gdb:metadata-widget-position';
const DRAG_THRESHOLD = 4;

type Position = { x: number; y: number };

const isBranch = (value: unknown): value is Record<string, unknown> | unknown[] => {
  return value !== null && typeof value === 'object';
};

const formatLeaf = (value: unknown): string => {
  if (typeof value === 'string') {
    return `"${value}"`;
  }

  return String(value);
};

const clamp = (value: number, min: number, max: number): number => Math.min(Math.max(value, min), max);

const MetadataNode = ({ name, value }: MetadataNodeProps) => {
  if (!isBranch(value)) {
    return (
      <div className={styles.leaf}>
        <span className={styles.key}>{name}:</span> <span className={styles.value}>{formatLeaf(value)}</span>
      </div>
    );
  }

  const entries: Array<[string, unknown]> = Array.isArray(value)
    ? value.map((item, index) => [String(index), item])
    : Object.entries(value);

  const summary = Array.isArray(value) ? `[...] ${value.length} items` : `{...} ${Object.keys(value).length} keys`;

  return (
    <details className={styles.branch}>
      <summary>
        <span className={styles.key}>{name}</span> <span className={styles.summary}>{summary}</span>
      </summary>
      <div className={styles.children}>
        {entries.map(([key, child]) => (
          <MetadataNode key={key} name={key} value={child} />
        ))}
      </div>
    </details>
  );
};

export const MetadataWidget = ({ entries }: MetadataWidgetProps) => {
  const rootRef = useRef<HTMLDetailsElement>(null);
  const dragState = useRef<{
    pointerId: number;
    startX: number;
    startY: number;
    originX: number;
    originY: number;
    dragging: boolean;
  } | null>(null);
  const wasDragged = useRef(false);
  const [position, setPosition] = useState<Position | null>(null);

  useEffect(() => {
    try {
      const stored = window.localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (typeof parsed?.x === 'number' && typeof parsed?.y === 'number') {
          setPosition(parsed);
        }
      }
    } catch {
      // ignore
    }
  }, []);

  if (!entries || entries.length === 0) {
    return null;
  }

  const onPointerDown = (e: ReactPointerEvent<HTMLElement>) => {
    if (e.button !== 0 || !rootRef.current) {
      return;
    }
    const rect = rootRef.current.getBoundingClientRect();
    dragState.current = {
      pointerId: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      originX: rect.left,
      originY: rect.top,
      dragging: false,
    };
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const onPointerMove = (e: ReactPointerEvent<HTMLElement>) => {
    const s = dragState.current;
    if (!s || s.pointerId !== e.pointerId || !rootRef.current) {
      return;
    }
    const dx = e.clientX - s.startX;
    const dy = e.clientY - s.startY;
    if (!s.dragging && Math.hypot(dx, dy) < DRAG_THRESHOLD) {
      return;
    }
    s.dragging = true;
    e.preventDefault();
    const el = rootRef.current;
    const x = clamp(s.originX + dx, 0, window.innerWidth - el.offsetWidth);
    const y = clamp(s.originY + dy, 0, window.innerHeight - el.offsetHeight);
    setPosition({ x, y });
  };

  const onPointerUp = (e: ReactPointerEvent<HTMLElement>) => {
    const s = dragState.current;
    if (!s || s.pointerId !== e.pointerId) {
      return;
    }
    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
    wasDragged.current = s.dragging;
    dragState.current = null;
    if (wasDragged.current && rootRef.current) {
      const rect = rootRef.current.getBoundingClientRect();
      try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ x: rect.left, y: rect.top }));
      } catch {
        // ignore
      }
    }
  };

  const onSummaryClick = (e: ReactMouseEvent<HTMLElement>) => {
    if (wasDragged.current) {
      e.preventDefault();
      wasDragged.current = false;
    }
  };

  const positionStyle: CSSProperties | undefined = position
    ? { left: position.x, top: position.y, right: 'auto', bottom: 'auto' }
    : undefined;

  return (
    <details ref={rootRef} className={styles.root} style={positionStyle}>
      <summary
        className={styles.handle}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onClick={onSummaryClick}
      >
        Page metadata ({entries.length})
      </summary>
      <div className={styles.panel}>
        {entries.map((entry, index) => (
          <MetadataNode key={`${entry.label}-${index}`} name={entry.label} value={entry.data} />
        ))}
      </div>
    </details>
  );
};
